import {
  EIGENFIELD,
  EIGENFIELD_FRAG,
  EIGENFIELD_SAMPLERS,
  EIGENFIELD_VERT,
  buildEigenfieldUniforms,
  designTokens,
  designTokensDark,
  hexToRgb,
  reflectForDark,
  type EigenfieldVariant,
  type TextBandFade,
  type ThemeName,
} from '@roomtreatment/design'

/**
 * The generated background, running live on WebGL2.
 *
 * Deliberately raw WebGL rather than three.js: this renders on marketing pages,
 * and pulling ~600 KB of scene graph in to draw one full-screen triangle would
 * cost more than the feature. Nothing here needs a camera, a mesh or a material.
 *
 * The field is not a video and not a bitmap under a transform. Each of the four
 * mode weights breathes at the room's own eigenfrequency, so every contour moves
 * independently — crowding, separating and bending. A rigid pan moves every line
 * with one shared vector and the eye reads that instantly as a moving photo. The
 * mode frequencies are incommensurate, so nothing ever repeats.
 */

const TEXTURE_BASE = '/eigenfield'

/** The web's paper, as linear-ish 0..1 RGB for the shader's `ground` uniform —
 *  per theme, because the ground is the one thing the shader is told rather
 *  than reading from a texture. */
const groundOf = (hex: string) => {
  const { r, g, b } = hexToRgb(hex)
  return new Float32Array([r / 255, g / 255, b / 255])
}
const GROUND: Record<ThemeName, Float32Array> = {
  light: groundOf(designTokens.paper),
  dark: groundOf(designTokensDark.paper),
}

const TEXTURE_FILES: Record<(typeof EIGENFIELD_SAMPLERS)[number], string> = {
  fieldABC: `${TEXTURE_BASE}/eigenfield-abc.png`,
  fieldABCFine: `${TEXTURE_BASE}/eigenfield-abc-fine.png`,
  fieldD: `${TEXTURE_BASE}/eigenfield-d.png`,
  lineLut: `${TEXTURE_BASE}/eigenfield-lut.png`,
}

export type EigenfieldOptions = {
  /** Where the lines thin so text below stays legible, in screen fractions. */
  artFade: TextBandFade
  /** Which superposition to draw (EIGENFIELD_VARIANTS). Omit for the baked
   *  recipe. Fixed for the life of the handle — it feeds a uniform, so changing
   *  it means a new handle, which is what the component's effect already does. */
  variant?: EigenfieldVariant
  /** Freeze the clock at t=0. The field still renders; it just stops breathing. */
  reducedMotion?: boolean
  /** Cap on device pixel ratio. 2 is plenty for line art and halves the fill
   *  cost on a 3x phone. */
  maxPixelRatio?: number
  /** Which ground the lines are drawn on, and which way the ink is lit. Fixed
   *  for the life of the handle, like `variant`. */
  theme?: ThemeName
}

export type EigenfieldHandle = {
  start(): void
  stop(): void
  destroy(): void
  /** Renders exactly one frame. Used for reduced motion and for tests. */
  renderOnce(seconds?: number): void
}

/**
 * Whether this browser can run the field at all.
 *
 * WebGL2 is required: the 16-bit field split needs `highp` in the fragment
 * stage, which WebGL1 does not guarantee. Callers fall back to the baked still.
 *
 * Deleted once as dead code when the component learned to rely on
 * `createEigenfield` throwing — but that made the failure path an async catch,
 * which means a synchronous `setState` inside an effect. Reading support
 * through `useSyncExternalStore` instead avoids that, and needs this.
 */
let cachedSupport: boolean | undefined

export function supportsEigenfield(canvas?: HTMLCanvasElement): boolean {
  if (canvas) {
    try {
      return !!canvas.getContext('webgl2')
    } catch {
      return false
    }
  }
  if (cachedSupport !== undefined) return cachedSupport
  try {
    const probe = document.createElement('canvas')
    const gl = probe.getContext('webgl2')
    cachedSupport = !!gl
    // Drop the probe context immediately — useSyncExternalStore reads this
    // on every render, and an uncached getContext would exhaust the browser's
    // WebGL budget (typically 8–16) and blank every canvas on the page.
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
    return cachedSupport
  } catch {
    cachedSupport = false
    return false
  }
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)
  if (!shader) throw new Error('could not create shader')
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    // The log is the only useful artefact when a shader fails; without it you
    // get a blank canvas and no indication why.
    throw new Error(`eigenfield shader failed to compile: ${gl.getShaderInfoLog(shader)}`)
  }
  return shader
}

/**
 * Re-inks the LUT's colour row (row 0) for the dark ground, in place on the
 * bound texture. Row 1 is data — per-strand duty and alpha — and is left
 * exactly as uploaded, which is why this is a sub-image write of one row
 * rather than a second full upload through a canvas (a 2D canvas round-trip
 * premultiplies, and would quantise any strand whose alpha is below 255).
 *
 * The line art is the one colour on the site that lives in a texture instead
 * of a token, so it cannot be themed by swapping a value; it is reflected
 * with the same ink ↔ page move the tokens make (`reflectForDark`).
 */
function reinkLutRow(gl: WebGL2RenderingContext, bitmap: ImageBitmap) {
  const w = bitmap.width
  const scratch = new OffscreenCanvas(w, 1)
  const ctx = scratch.getContext('2d')
  if (!ctx) return
  ctx.drawImage(bitmap, 0, 0)
  const row = ctx.getImageData(0, 0, w, 1).data
  for (let i = 0; i < row.length; i += 4) {
    const { r, g, b } = reflectForDark({ r: row[i]!, g: row[i + 1]!, b: row[i + 2]! })
    row[i] = r
    row[i + 1] = g
    row[i + 2] = b
  }
  gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, w, 1, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(row.buffer))
}

async function loadTexture(
  gl: WebGL2RenderingContext,
  url: string,
  unit: number,
  smooth: boolean,
  reink = false,
) {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`eigenfield texture ${url}: ${response.status}`)

  // These PNGs are not photographs — each channel is a raw encoded number
  // (fieldABC/fieldABCFine sum additively to a 16-bit displacement per mode;
  // the LUT's RGB is ink colour meant to be read back byte-for-byte). Browsers
  // colour-manage image decode AND WebGL upload by default, which silently
  // remaps those channel values against the image's embedded/assumed colour
  // profile. That is precisely what corrupted this on first render: the field
  // shape drifted (the additive reconstruction no longer summed to the
  // original value) and the ink came out shifted toward magenta instead of
  // the palette's ink-blue. Both `colorSpaceConversion: 'none'` here and
  // `UNPACK_COLORSPACE_CONVERSION_WEBGL` below are required — one governs
  // decode, the other the texture upload, and either alone still corrupts.
  //
  // `imageOrientation: 'none'` plus UNPACK_FLIP_Y off keeps row 0 of the PNG at
  // v=0, which is where Skia samples it. Flipping either would mirror the field.
  const bitmap = await createImageBitmap(await response.blob(), {
    imageOrientation: 'none',
    colorSpaceConversion: 'none',
    premultiplyAlpha: 'none',
  })

  // Everything from here down is synchronous, so no other loader can interleave
  // and steal the active texture unit — which is exactly what used to happen.
  //
  // The four loaders run concurrently under Promise.all. Binding to "whatever
  // unit happens to be active" meant each loader's tail clobbered the unit a
  // previously-resolved loader had just set up, and the bindings ended up
  // scrambled: the lineLut sampler read one of the mode-field textures instead.
  // That renders as plausible-looking line art in completely wrong colours,
  // because a field texture's RGB is encoded displacement, not ink — the ink it
  // produced, (109,56,97), does not appear anywhere in the real LUT.
  //
  // Selecting the unit here, inside the atomic tail, makes the binding
  // deterministic regardless of completion order.
  const texture = gl.createTexture()
  gl.activeTexture(gl.TEXTURE0 + unit)
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false)
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap)
  if (reink) reinkLutRow(gl, bitmap)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  // The line lookup is indexed, not interpolated — filtering it would blend one
  // strand's width and colour into its neighbour's.
  const filter = smooth ? gl.LINEAR : gl.NEAREST
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter)
  bitmap.close()
  return texture
}

/**
 * How much of the field one element covers, in units of the design frame.
 *
 * The field is anchored to CSS PIXELS, not to the element's aspect ratio. An
 * element 1080 CSS px wide shows exactly one design frame across; one twice
 * that wide shows two. Line spacing in CSS pixels is therefore the same
 * everywhere, and the field scales with the page exactly as text does.
 *
 * This replaced an aspect-only cover-fit that always showed the full field
 * width whatever the element measured, which had two visible faults:
 *
 *   - Browser zoom rescaled the art. Zooming halves the element's width in CSS
 *     px, so the same fifty stripes were squeezed into half the space and the
 *     lines visibly fattened. The background appeared to zoom independently of
 *     the page, which reads as a bug because it is one.
 *   - It cropped hard. On a wide hero the vertical span collapsed to ~23% of
 *     the frame, so the page showed a handful of fat curves instead of the fine
 *     contour field the app draws.
 *
 * Aspect is undistorted by construction: each axis is divided by its own
 * reference dimension, so the field keeps the anisotropy it was authored with.
 */
export function fieldScale(width: number, height: number): [number, number] {
  return [
    Math.max(width, 1) / EIGENFIELD.referenceWidth,
    Math.max(height, 1) / EIGENFIELD.referenceHeight,
  ]
}

/** Contour spacing in CSS pixels — the number the zoom invariant is stated in. */
export function lineSpacingPx(width: number): number {
  const [scaleX] = fieldScale(width, 1)
  return width / (EIGENFIELD.freq * scaleX)
}

export async function createEigenfield(
  canvas: HTMLCanvasElement,
  options: EigenfieldOptions,
): Promise<EigenfieldHandle> {
  const gl = canvas.getContext('webgl2', {
    // `alpha: false` is the usual choice for an opaque full-screen effect and it
    // is wrong here. With no alpha channel an undrawn buffer composites as
    // opaque BLACK — so between mount and first paint (or forever, if texture
    // loading fails after the context is created) the hero is black with dark
    // ink text on it, which is unreadable. With alpha the undrawn canvas is
    // transparent and the ground colour behind it shows through, so the worst
    // case is paper with no lines rather than an unreadable slab.
    alpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    // Lets the buffer survive compositing so it can be read back — which is how
    // the UI proofs capture it, and the repo's rule is to look at the picture.
    preserveDrawingBuffer: true,
    powerPreference: 'low-power',
  })
  if (!gl) throw new Error('WebGL2 unavailable')
  const theme = options.theme ?? 'light'

  const program = gl.createProgram()
  if (!program) throw new Error('could not create program')
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, EIGENFIELD_VERT))
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, EIGENFIELD_FRAG))
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`eigenfield program failed to link: ${gl.getProgramInfoLog(program)}`)
  }
  gl.useProgram(program)

  const textures = await Promise.all(
    EIGENFIELD_SAMPLERS.map((name, i) =>
      loadTexture(gl, TEXTURE_FILES[name], i, name !== 'lineLut', name === 'lineLut' && theme === 'dark'),
    ),
  )

  // Sampler uniforms are set only once every texture is in place, so a partially
  // loaded set can never be pointed at.
  EIGENFIELD_SAMPLERS.forEach((name, i) => {
    gl.uniform1i(gl.getUniformLocation(program, name), i)
  })

  const locations = new Map<string, WebGLUniformLocation | null>()
  const location = (name: string) => {
    if (!locations.has(name)) locations.set(name, gl.getUniformLocation(program, name))
    return locations.get(name) ?? null
  }

  const maxRatio = options.maxPixelRatio ?? 2
  let width = 0
  let height = 0
  let pixelRatio = 1

  const resize = () => {
    const rect = canvas.getBoundingClientRect()
    pixelRatio = Math.min(window.devicePixelRatio || 1, maxRatio)
    const w = Math.max(1, Math.round(rect.width))
    const h = Math.max(1, Math.round(rect.height))
    if (w === width && h === height) return
    width = w
    height = h
    canvas.width = Math.round(w * pixelRatio)
    canvas.height = Math.round(h * pixelRatio)
    gl.viewport(0, 0, canvas.width, canvas.height)
  }

  const renderOnce = (seconds = 0) => {
    resize()
    const uniforms = buildEigenfieldUniforms({
      width,
      height,
      pixelRatio,
      artFade: options.artFade,
      variant: options.variant,
      seconds,
    })

    for (const [name, value] of Object.entries(uniforms)) {
      const at = location(name)
      if (at === null) continue
      if (typeof value === 'number') gl.uniform1f(at, value)
      else if (value.length === 2) gl.uniform2fv(at, value)
      else if (value.length === 3) gl.uniform3fv(at, value)
      else if (value.length === 4) gl.uniform4fv(at, value)
    }
    gl.uniform2fv(location('uvScale'), fieldScale(width, height))
    // The baked recipe paints its lines on the app's warm bone. On the web the
    // surrounding page is cool, and the seam between the two is the single most
    // visible temperature clash on the site — a creamy rectangle sitting inside
    // a glacier page. Repainting the ground to the web's paper closes it. The
    // ink is untouched: only what the lines sit on changes. (In dark the ink
    // is re-lit at texture load — see reinkLutRow.)
    gl.uniform3fv(location('ground'), GROUND[theme])

    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }

  let frame = 0
  let running = false
  let lastDraw = 0
  const start = () => {
    if (running || options.reducedMotion) return
    running = true
    const loop = (now: number) => {
      if (!running) return
      // The slowest mode is 45s and the modulation is ±1.5%; 60fps would be
      // spending a phone's battery to redraw a picture that has not changed.
      if (now - lastDraw >= 1000 / 30) {
        lastDraw = now
        renderOnce(now / 1000)
      }
      frame = requestAnimationFrame(loop)
    }
    frame = requestAnimationFrame(loop)
  }

  const stop = () => {
    running = false
    if (frame) cancelAnimationFrame(frame)
    frame = 0
  }

  const destroy = () => {
    stop()
    textures.forEach((texture) => gl.deleteTexture(texture))
    gl.deleteProgram(program)
    gl.getExtension('WEBGL_lose_context')?.loseContext()
  }

  renderOnce(0)
  return { start, stop, destroy, renderOnce }
}
